import React from 'react';
import { alpha, Box, Stack, Typography } from '@mui/material';

import type { CleanupSnapshot } from '../types';

export interface Step0SnapshotProps {
  snapshot: CleanupSnapshot;
  /** Quick-win sets ready to one-click (computed by the wizard). */
  quickWinSetCount?: number;
}

const Stat: React.FC<{
  value: number;
  label: string;
  tone?: 'default' | 'good' | 'attention';
}> = ({ value, label, tone = 'default' }) => (
  <Box
    sx={(theme) => ({
      flex: '1 1 150px',
      minWidth: 140,
      border: `1px solid ${
        tone === 'good'
          ? alpha(theme.palette.success.main, 0.35)
          : tone === 'attention'
            ? alpha(theme.palette.warning.main, 0.4)
            : theme.palette.divider
      }`,
      borderRadius: 1.5,
      bgcolor:
        tone === 'good'
          ? alpha(theme.palette.success.main, 0.05)
          : tone === 'attention'
            ? alpha(theme.palette.warning.main, 0.05)
            : theme.palette.background.paper,
      px: 2,
      py: 1.5,
    })}
  >
    <Typography
      variant="h5"
      sx={{
        fontWeight: 700,
        color:
          tone === 'good'
            ? 'success.dark'
            : tone === 'attention'
              ? 'warning.dark'
              : 'primary.dark',
      }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

/**
 * Wizard Step 0 (GDN-2235): orientation snapshot — the practice's own
 * numbers before asking for anything. Dead types were already auto-archived
 * (Layer 1) — reported here, reviewed next step. Pure presentation.
 */
export const Step0Snapshot: React.FC<Step0SnapshotProps> = ({
  snapshot,
  quickWinSetCount = 0,
}) => (
  <Box>
    <Typography variant="h6" sx={{ mb: 0.5 }}>
      Your practice&apos;s appointment types
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 2, maxWidth: 640 }}
    >
      Appointment types drive each visit&apos;s note template, billing,
      duration, and forms &amp; reminders.
      <br />
      Right now they&apos;re scattered across your providers — let&apos;s
      tidy them into one shared catalog.
    </Typography>

    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
      <Stat
        value={snapshot.totalTypes}
        label={`appointment types across ${snapshot.providerCount} providers`}
      />
      <Stat
        value={snapshot.archivedCount}
        label="unused types already archived for you"
        tone="good"
      />
      <Stat
        value={snapshot.autoPromoteCount + snapshot.autoMergeCount}
        label="identical or already-shareable — handled automatically"
        tone="good"
      />
      {quickWinSetCount > 0 && (
        <Stat
          value={quickWinSetCount}
          label="quick wins ready to consolidate in one click"
          tone="good"
        />
      )}
      <Stat
        value={snapshot.divergentGroupCount + snapshot.personalClusterCount}
        label={`groups need your review (${snapshot.divergentGroupCount} duplicates + ${snapshot.personalClusterCount} personal copies)`}
        tone="attention"
      />
    </Stack>

    <Box
      sx={(theme) => ({
        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
        mb: 1.5,
        maxWidth: 720,
      })}
    >
      <Typography variant="caption" sx={{ color: 'primary.dark' }}>
        ⓘ <b>Type vs Template:</b> the appointment type sets up the visit —
        duration, billing, forms, and which note template to chart with. The
        note template shapes the note JotPsych writes from the visit.
      </Typography>
    </Box>

    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        mb: 2.5,
        maxWidth: 720,
        bgcolor: theme.palette.background.paper,
      })}
    >
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', lineHeight: 1.8 }}
      >
        What&apos;s ahead
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ '& > div': { mb: 0.25 } }}
      >
        <div>
          <b>Archived</b> — skim what was auto-archived; rescue anything
          you still want.
        </div>
        <div>
          <b>Quick wins</b> — sets your providers already agree on
          (identical setups, names only differ); consolidate in one click.
        </div>
        <div>
          <b>Duplicates</b> — same-named types that genuinely differ;
          pick which version the practice shares.
        </div>
        <div>
          <b>Personal copies</b> — providers&apos; own types on their
          own templates; sharing is opt-in — promote a pair only if the
          whole practice should use it.
        </div>
        <div>
          <b>Loose ends</b> — anything that ends up interchangeable after
          merging gets one final pass. <b>Don&apos;t sweat near-misses along
          the way</b> — you can consolidate at the end, and you&apos;ll get a
          per-provider summary of every rename to share with your group.
        </div>
      </Typography>
    </Box>

  </Box>
);
